import React, { useEffect, useState } from 'react'
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import './EditOrderModal.css';
import { onValue, ref } from 'firebase/database';
import { db } from '../../firebase';

const EditOrderModal = ({ show, handleClose, handleUpdateOrder, orderToEdit }) => {
    const [invoice_number, setInvoiceNumber] = useState('');
    const [orderDescription, setOrderDescription] = useState('');
    const [reference, setReference] = useState('');
    const [orderStatus, setOrderStatus] = useState('');
    const [products, setProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [allProducts, setAllProducts] = useState([]); // Array to hold all products from the database
    const [searchResults, setSearchResults] = useState([]); // Array to hold filtered search results
    const [loading, setLoading] = useState(true);
    const [assembledBy, setAssembledBy] = useState(null);
    const [completedBy, setCompletedBy] = useState(null);
    const user = localStorage.getItem("admin_name") ? localStorage.getItem("admin_name") : localStorage.getItem('admin_email');

    // Fetch all products from the database
    useEffect(() => {
        const fetchProducts = () => {
            const productsRef = ref(db, 'Products/');
            onValue(productsRef, (snapshot) => {
                if (snapshot.val() !== null) {
                    const productsData = Object.entries(snapshot.val()).map(([id, product]) => ({
                        id,
                        ...product,
                    }));
                    setAllProducts(productsData);
                }
                setLoading(false);

            }, (error) => {
                console.error('Error fetching products:', error);
                setLoading(false);
            });
        };
        fetchProducts();
    }, []);

    // Update search results based on the search term
    useEffect(() => {
        if (searchTerm) {
            const filteredResults = allProducts.filter((product) =>
                (product.name && product.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (product.mpn && product.mpn.toLowerCase().includes(searchTerm.toLowerCase())) || // Example for ID search
                (product.supplier && product.supplier.toLowerCase().includes(searchTerm.toLowerCase()))
            );
            setSearchResults(filteredResults);
        } else {
            setSearchResults([]);
        }
    }, [searchTerm, allProducts]);

    useEffect(() => {
        if (orderToEdit) {
            console.log('Order to edit:', orderToEdit);
            setInvoiceNumber(orderToEdit.invoice_number || '');
            setReference(orderToEdit.reference || "");
            setOrderDescription(orderToEdit.order_description || '');
            setOrderStatus(orderToEdit.order_status || '');
            setAssembledBy(orderToEdit.assembled_by || null);
            setCompletedBy(orderToEdit.completed_by || null);

            // Safely handle products property
            setProducts(
                Array.isArray(orderToEdit.products)
                    ? orderToEdit.products.map((product) => ({
                        ...product,
                        is_assembled: product.is_assembled || false, // Ensure property exists
                    }))
                    : []
            );
        }
    }, [orderToEdit]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleAddProduct = (product) => {
        if (!product.mpn) {
            alert('Product must have a valid MPN.');
            return;
        }
        // Ensure the product is not already in the selected products list
        if (products.some((p) => p.mpn === product.mpn)) {
            alert('Product already added to the list.');
            return;
        }

        setProducts((prevProducts) => [
            ...prevProducts,
            {
                name: product.name || '',
                mpn: product.mpn || '',
                quantity: 1,
                is_assembled: false, // Default to false
            },
        ]
        );

        setSearchTerm('');
        setSearchResults([]);
    };

    const handleProductChange = (index, field, value) => {
        const updatedProducts = [...products];

        if (field === 'name') {
            // Find the selected product details from the allProducts array
            const selectedProduct = allProducts.find((product) => product.name === value);

            if (selectedProduct) {
                // Check if this product is already added
                if (products.some((p, idx) => idx !== index && p.mpn === selectedProduct.mpn)) {
                    alert('This product is already added to the list.');
                    return;
                }

                updatedProducts[index].mpn = selectedProduct.mpn || ''; // Update MPN
                updatedProducts[index].name = selectedProduct.name || ''; // Update Name
            }
        } else {
            updatedProducts[index][field] = value;
        }

        setProducts(updatedProducts);
    };

    const addProduct = () => {
        // Create a placeholder product with default values
        const newProduct = { name: '', mpn: '', quantity: 1, is_assembled: false };

        // Check if the product already exists in the list based on its mpn
        if (products.some((p) => p.mpn === newProduct.mpn)) {
            alert('This product already exists in the list pppp.');
            return;
        }

        // Add the new product to the list
        setProducts((prevProducts) => [...prevProducts, newProduct]);
    }

    const removeProduct = (index) => {
        setProducts(products.filter((_, i) => i !== index));
    }

    const handleCheckboxChange = (productId) => {
        setProducts((prevProducts) =>
            prevProducts.map((product) =>
                product.mpn === productId
                    ? { ...product, is_assembled: !product.is_assembled }
                    : product

            )
        );
    };

    const handleSubmit = () => {
        // Ensure order name is provided
        if (!invoice_number.trim()) {
            alert('Order name is required.');
            return;
        }

        // Filter out empty products (e.g., products with no name or zero quantity)
        const validProducts = products.filter(
            product => product.name && product.quantity > 0 && product.mpn);
        if (validProducts.length === 0) {
            alert('Please add at least one product before submitting.');
            return;
        }

        let assembledByValue = assembledBy;
        let completedByValue = completedBy;
        if (orderStatus === 'In Progress') {
            assembledByValue = user; // Directly assign user to assembledByValue
        } else if (orderStatus === 'Completed') {
            completedByValue = user; // Directly assign user to completedByValue
        }

        const updatedOrder = {
            ...orderToEdit,
            invoice_number: invoice_number,
            reference: reference,
            order_description: orderDescription,
            order_status: orderStatus,
            assembled_by: assembledByValue,
            completed_by: completedByValue,
            last_updated_at: new Date().toISOString(),
            products: validProducts,

        };
        handleUpdateOrder(updatedOrder);
    };



    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Edit Order</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group controlId="orderName">
                        <Form.Label>Invoice Number</Form.Label>
                        <Form.Control
                            type="text"
                            value={invoice_number}
                            onChange={(e) => setInvoiceNumber(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group controlId="orderName">
                        <Form.Label>Reference </Form.Label>
                        <Form.Control
                            type="text"
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group controlId='formOrderDescription' className='mt-3'>
                        <Form.Label>Order Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            value={orderDescription}
                            onChange={(e) => setOrderDescription(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group controlId='formOrderStatus' className='mt-3'>
                        <Form.Label>Order Status</Form.Label>
                        <Form.Control
                            as="select"
                            value={orderStatus}
                            onChange={(e) => setOrderStatus(e.target.value)}
                        >
                            <option value="Start">Start</option>
                            {/* <option value="initiate">Initiate</option> */}
                            <option value="In Progress">In Progress</option>
                            <option value="Completed">Completed</option>
                        </Form.Control>
                    </Form.Group>
                    {/* Search and Add Product */}
                    <Form.Group controlId="formSearchProduct" className="mt-3">
                        <Form.Label>Search Products</Form.Label>
                        <Form.Control
                            type="text"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            placeholder="Search by mpn, name or supplier"
                        />
                    </Form.Group>
                    {loading ? <Spinner animation="border" /> :
                        <div className="search-results">

                            {/* <div className="search-results"> */}
                            {searchResults.map((product) => (
                                <div key={product.id} className="search-result-item">
                                    <span>{product.name} (Supplier: {product.supplier})</span>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => handleAddProduct(product)}
                                    >
                                        Add
                                    </Button>
                                </div>
                            ))}
                        </div>
                    }


                    <Form.Group controlId="formProducts" className="mt-3">
                        <Form.Label>Products</Form.Label>
                        {products.length > 0 ? (
                            <ul>
                                {products.map((product, index) => (
                                    <div key={index} className="product-item mb-2">
                                        <Form.Check
                                            type="checkbox"
                                            label={`Is Assembled? ${product.mpn}  (${product.name || 'Unnamed Product'})`}
                                            checked={product.is_assembled || false}
                                            onChange={() => handleCheckboxChange(product.mpn)}
                                        />
                                        <Form.Control
                                            type="text"
                                            value={product.mpn}
                                            placeholder="MPN"
                                            readOnly
                                            className="mb-1"
                                        />
                                        <Form.Control
                                            as="select"
                                            value={product.name}
                                            onChange={(e) => handleProductChange(index, 'name', e.target.value)}
                                            className="mb-1"
                                        >
                                            <option value="">Select Product</option>
                                            {allProducts.map((availableProduct) => (
                                                <option key={availableProduct.id} value={availableProduct.name}>
                                                    {availableProduct.name} (Supplier: {availableProduct.supplier})
                                                </option>
                                            ))}
                                        </Form.Control>
                                        <Form.Control
                                            type="number"
                                            placeholder="Quantity"
                                            min={1}
                                            value={product.quantity}
                                            onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                                            className="mb-1"
                                        />
                                        <Button variant="danger" onClick={() => removeProduct(index)}>
                                            Remove
                                        </Button>
                                    </div>
                                ))}
                            </ul>
                        ) : (
                            <p>No Products available</p>
                        )}
                        <Button variant="success" onClick={addProduct} className="mt-2">
                            Add Product
                        </Button>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
                <Button variant="primary"
                    onClick={handleSubmit}>
                    Save Changes
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default EditOrderModal